/*
 * Collection infinite scroll
 *
 * Progressive enhancement on top of the theme's normal ?page=N pagination:
 *  - Without JS (and for crawlers) the standard pagination links and the
 *    "Load more products" link are plain <a href="?page=N"> links, so every
 *    page stays crawlable and indexable.
 *  - With JS, the next page is fetched via the Section Rendering API as the
 *    shopper nears the bottom of the grid and its products are appended, until
 *    there are no more pages.
 *  - The address bar is kept in sync (history.replaceState) with the page whose
 *    products are currently in view, so refreshing or sharing lands on the
 *    right page — the pattern Google recommends for infinite scroll.
 *
 * The element lives inside [data-products-grid], which the theme re-renders on
 * filter / sort changes, so a fresh instance connects after every refresh.
 */
(function () {
  if (window.customElements.get('collection-infinite-scroll')) return;

  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.gridOuter = this.closest('[data-products-grid]');
      this.grid = this.gridOuter && this.gridOuter.querySelector('#CollectionLoop');
      this.nextUrl = this.dataset.nextUrl || '';
      this.sectionId = this.dataset.sectionId;
      this.loadMore = this.querySelector('[data-infinite-load-more]');
      this.status = this.querySelector('[data-infinite-status]');
      this.loading = false;

      if (!this.grid || !this.sectionId) return;

      // JS is running: hide the numbered pagination and let scrolling take over.
      this.gridOuter.classList.add('has-infinite-scroll');

      this.onLoadMoreClick = (event) => {
        event.preventDefault();
        this.loadNext();
      };
      if (this.loadMore) this.loadMore.addEventListener('click', this.onLoadMoreClick);

      this.markPage(this.grid.querySelector('.grid-item:not(.product-item--featured-image)'), window.location.href);
      this.observePageMarkers();

      if (!this.nextUrl) {
        this.finish();
        return;
      }

      if ('IntersectionObserver' in window) {
        this.classList.add('is-auto');
        this.sentinelObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
          },
          { rootMargin: '0px 0px 800px 0px' }
        );
        this.sentinelObserver.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.sentinelObserver) this.sentinelObserver.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.loadMore) this.loadMore.removeEventListener('click', this.onLoadMoreClick);
    }

    loadNext() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      this.classList.add('is-loading');

      const pageUrl = new URL(this.nextUrl, window.location.origin);
      const fetchUrl = new URL(pageUrl.href);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      fetch(fetchUrl.href, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newGrid = doc.querySelector('#CollectionLoop');
          const newItems = newGrid
            ? Array.from(newGrid.children).filter((el) => el.classList.contains('grid-item') && !el.classList.contains('product-item--featured-image'))
            : [];

          // Avoid duplicates if the catalogue shifted between requests.
          const fragment = document.createDocumentFragment();
          let added = 0;
          newItems.forEach((item) => {
            if (item.id && document.getElementById(item.id)) return;
            fragment.appendChild(document.importNode(item, true));
            added += 1;
          });

          const firstNew = fragment.firstElementChild;
          this.grid.appendChild(fragment);
          this.markPage(firstNew, pageUrl.href);

          const nextHolder = doc.querySelector('collection-infinite-scroll');
          this.nextUrl = (nextHolder && nextHolder.dataset.nextUrl) || '';
          if (this.loadMore) {
            if (this.nextUrl) this.loadMore.setAttribute('href', this.nextUrl);
          }

          // Keep the (hidden) numbered pagination in step for crawlers that render JS.
          const newPagination = doc.querySelector('[data-products-grid] .pagination-custom');
          const currentPagination = this.gridOuter.querySelector('.pagination-custom');
          if (newPagination && currentPagination) currentPagination.replaceWith(document.importNode(newPagination, true));

          if (this.status) this.status.textContent = `${added} more products loaded`;

          document.dispatchEvent(new CustomEvent('theme:collection:products-appended', { detail: { count: added } }));

          if (!this.nextUrl) this.finish();
        })
        .catch(() => {
          // Fall back to a manual button; its href still points at the next page.
          this.classList.remove('is-auto');
          this.classList.add('has-error');
          if (this.sentinelObserver) this.sentinelObserver.disconnect();
        })
        .finally(() => {
          this.loading = false;
          this.classList.remove('is-loading');
          // If the new content didn't fill the viewport, keep going.
          if (this.nextUrl && this.sentinelObserver && this.isNearViewport()) {
            window.requestAnimationFrame(() => this.loadNext());
          }
        });
    }

    isNearViewport() {
      const rect = this.getBoundingClientRect();
      return rect.top - window.innerHeight < 800;
    }

    finish() {
      this.nextUrl = '';
      this.classList.add('is-complete');
      this.classList.remove('is-auto');
      if (this.sentinelObserver) this.sentinelObserver.disconnect();
    }

    // Tag the first product of each page so the URL can follow the scroll.
    markPage(el, url) {
      if (!el) return;
      const clean = new URL(url, window.location.origin);
      clean.searchParams.delete('section_id');
      el.setAttribute('data-infinite-page-url', clean.pathname + clean.search);
    }

    observePageMarkers() {
      if (!window.history.replaceState) return;
      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          this.syncUrl();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    // The page in view is the last page marker above the middle of the viewport.
    syncUrl() {
      const markers = this.grid.querySelectorAll('[data-infinite-page-url]');
      if (!markers.length) return;
      const middle = window.innerHeight / 2;
      let url = markers[0].getAttribute('data-infinite-page-url');
      markers.forEach((el) => {
        if (el.getBoundingClientRect().top <= middle) url = el.getAttribute('data-infinite-page-url');
      });
      const current = window.location.pathname + window.location.search;
      if (url && url !== current) window.history.replaceState(window.history.state, '', url);
    }
  }

  window.customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
})();
